import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import type { User } from "@ferrivia/api-contract";
import { FloatingTabBarShell, useOnline } from "@ferrivia/ui";
import { useTracker } from "@ferrivia/auth-web";
import { useMarket } from "@ferrivia/i18n";
import { markBootReady } from "../boot/bootSplash";
import { OrderConfirmationDialog } from "../components/IronPanelPurchase";
import { NavRail } from "../components/NavRail";
import { TabBar } from "../components/TabBar";
import { ChatSummaryProvider } from "../hooks/ChatSummaryProvider";
import { useEnsurePatientRelationship } from "../hooks/useEnsurePatientRelationship";
import { useKeyboardVisible } from "../hooks/useKeyboardVisible";
import { useScreenBackground } from "../hooks/useScreenBackground";
import { QuestionnaireOverlay } from "../questionnaire/QuestionnaireOverlay";
import { SummaryOverlay } from "../summary/SummaryOverlay";
import { OfflineBanner } from "./OfflineBanner";
import { TabNavigationProvider } from "./TabNavigation";
import { TabPanels } from "./TabPanels";
import { AppContext, type AppOutletContext, type OpenSummaryOptions } from "./useAppContext";
import s from "./AppShell.module.css";

export interface AppShellProps {
  user: User;
  signOut: () => void;
}

export function AppShell({ user, signOut }: AppShellProps) {
  useScreenBackground("app", "--color-surface-default");
  const tracker = useTracker();
  const market = useMarket();
  const { online, reportFailure, reportSuccess } = useOnline();
  useEnsurePatientRelationship({
    onTransportResult: (ok) => (ok ? reportSuccess() : reportFailure()),
  });

  const wasOnline = useRef(online);
  useEffect(() => {
    if (wasOnline.current === online) return;
    wasOnline.current = online;
    tracker.track(online ? "app.came_online" : "app.went_offline", {});
  }, [online, tracker]);
  // The keyboard already eats most of the screen; the tab bar can't be reached
  // under it anyway, so it gives up its space for as long as the keyboard is
  // there. Both halves of the pattern take it: the shell stops reserving the
  // footprint, the pill slides out of the way (see @ferrivia/ui).
  const keyboardVisible = useKeyboardVisible();
  // State, not a plain ref: a Sheet mounted on first render needs the node
  // to trigger a re-render once it's set, or it portals nowhere the first
  // time it opens.
  const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null);
  // The overlay itself decides what it looks like; this shell only knows
  // whether one is open, for tab-bar suppression (below) and for closing it
  // when ScreenerSheet's CTA (or any future opener) asks.
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false);
  const [overlayClosedCount, setOverlayClosedCount] = useState(0);
  const [summary, setSummary] = useState<({ id: string } & OpenSummaryOptions) | null>(null);
  const [pendingSummary, setPendingSummary] = useState<({ id: string } & OpenSummaryOptions) | null>(null);

  // The questionnaire hands off to the summary as it closes. Opening the
  // summary in the same commit would race its history push against the
  // questionnaire's pop, and the pop would take the summary's entry with it.
  const location = useLocation();
  const questionnaireEntry = ((location.state as { overlay?: string[] } | null)?.overlay ?? []).includes(
    "questionnaire",
  );
  useEffect(() => {
    if (pendingSummary && !questionnaireOpen && !questionnaireEntry) {
      setSummary(pendingSummary);
      setPendingSummary(null);
    }
  }, [pendingSummary, questionnaireOpen, questionnaireEntry]);

  // Readiness means "the shell painted", not "home's data arrived" — not
  // every panel's data has to be in, and tying dismissal to any one panel
  // would make the signal depend on which tab the URL happened to be on.
  // requestAnimationFrame, not the effect itself: dismissing on the frame
  // after commit avoids a splash -> white flash before this shell paints.
  useEffect(() => {
    const id = requestAnimationFrame(() => markBootReady("app"));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <AppContext.Provider
      value={
        {
          user,
          signOut,
          rootElement,
          openQuestionnaire: () => setQuestionnaireOpen(true),
          openSummary: (id, options) => setSummary({ id, ...options }),
          overlayClosedCount,
        } satisfies AppOutletContext
      }
    >
      <TabNavigationProvider>
        {/* Above the panels and the bar both: the unread count reaches the
            chat tab's badge and home's pinned notification, and they have to
            be the same number from the same fetch. */}
        <ChatSummaryProvider>
          {!online && <OfflineBanner />}
          <FloatingTabBarShell
            ref={setRootElement}
            className={s.root}
            collapsed={keyboardVisible || questionnaireOpen || summary !== null}
          >
            <NavRail />
            <div className={s.content}>
              <TabPanels />
            </div>
            <TabBar collapsed={keyboardVisible || questionnaireOpen || summary !== null} />
          </FloatingTabBarShell>
          <QuestionnaireOverlay
            open={questionnaireOpen}
            onClose={() => {
              setQuestionnaireOpen(false);
              setOverlayClosedCount((count) => count + 1);
            }}
            onOpenSummary={(id) => setPendingSummary({ id, source: "submitted" })}
          />
          <SummaryOverlay
            summary={summary}
            onClose={() => {
              setSummary(null);
              setOverlayClosedCount((count) => count + 1);
            }}
          />
          {/* Here rather than on a tab: Stripe returns to whatever success
              URL the session was opened with, including /store from a
              checkout started before that tab was removed. */}
          {market === "fi" && <OrderConfirmationDialog />}
        </ChatSummaryProvider>
      </TabNavigationProvider>
    </AppContext.Provider>
  );
}
